"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useState, useEffect, useMemo, createContext, useContext } from "react";
import { usePathname } from "next/navigation";
import { isEmbeddedIntegrationPath } from "@/lib/monday-app/embedded-path";
import { SmoothScroll } from "@/components/SmoothScroll";
import { isFunnelPath } from "@/lib/navigation/funnel-paths";
import { isTeamContactPath } from "@/lib/team/paths";
import { TenantProvider, type TenantConfig } from "@/lib/tenant";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ConsentProvider } from "@/lib/consent";
import { AnalyticsProvider } from "@/lib/analytics";
import { LoginModal } from "@/components/auth";
import { ProfileModal } from "@/components/profile";
import { Header } from "@/components/layout/Header";
import { PageFooter } from "@/components/layout/PageFooter";
import { LandvestHandoffContext } from "@/components/providers/LandvestHandoffContext";
import { CookieConsentBanner } from "@/components/consent";
import { ImpersonationBanner } from "@/components/admin/ImpersonationBanner";
import { GoogleAdsTag } from "@/components/google-ads/GoogleAdsTag";
import { TurnstileProvider } from "@/lib/turnstile";
import { ChatWidget } from "@/components/chat";

// ---------------------------------------------------------------------------
// HeaderContext — allows child layouts (e.g. funnel pages) to hide the global
// Header while keeping it visible everywhere else by default.
// ---------------------------------------------------------------------------
const HeaderContext = createContext<{
  visible: boolean;
  setVisible: (v: boolean) => void;
}>({
  visible: true,
  setVisible: () => {}
});

/**
 * Hook to hide the global Header for the lifetime of the calling component.
 * Restores visibility on unmount.
 */
export function useHideHeader() {
  const $ = _c(3);
  const {
    setVisible
  } = useContext(HeaderContext);
  let t0;
  let t1;
  if ($[0] !== setVisible) {
    t0 = () => {
      setVisible(false);
      return () => setVisible(true);
    };
    t1 = [setVisible];
    $[0] = setVisible;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}

// FooterContext — same pattern as HeaderContext for hiding the global footer
const FooterContext = createContext<{
  visible: boolean;
  setVisible: (v: boolean) => void;
}>({
  visible: true,
  setVisible: () => {}
});

/** Hide the global Footer for the lifetime of the calling component. */
export function useHideFooter() {
  const $ = _c(3);
  const {
    setVisible
  } = useContext(FooterContext);
  let t0;
  let t1;
  if ($[0] !== setVisible) {
    t0 = () => {
      setVisible(false);
      return () => setVisible(true);
    };
    t1 = [setVisible];
    $[0] = setVisible;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}

// ---------------------------------------------------------------------------
// SlimConsentContext — pages with a bottom-anchored primary CTA (funnel and
// post-funnel surfaces) opt into the slim top-strip consent banner instead
// of the default bottom sheet, which would occlude the CTA on mobile
// (EXP-007 / HYP-009 A3). Wired from FunnelHeader so any page rendering it
// inherits the behaviour automatically.
// ---------------------------------------------------------------------------
const SlimConsentContext = createContext<{
  enabled: boolean;
  setEnabled: (v: boolean) => void;
}>({
  enabled: false,
  setEnabled: () => {}
});

/**
 * Opt the current page into the slim top-strip consent banner variant for
 * the lifetime of the calling component. Restores the default bottom-sheet
 * variant on unmount.
 */
export function useSlimConsent() {
  const $ = _c(3);
  const {
    setEnabled
  } = useContext(SlimConsentContext);
  let t0;
  let t1;
  if ($[0] !== setEnabled) {
    t0 = () => {
      setEnabled(true);
      return () => setEnabled(false);
    };
    t1 = [setEnabled];
    $[0] = setEnabled;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}

/** Read the current slim-consent flag (used by CookieConsentBanner). */
export function useIsSlimConsent() {
  return useContext(SlimConsentContext).enabled;
}

/**
 * Render-nothing client component that hides the global Header.
 * Use in server-component layouts:
 *
 *   import { HideHeader } from "@/components/providers";
 *   export default function Layout({ children }) {
 *     return <><HideHeader />{children}</>;
 *   }
 */
export function HideHeader() {
  useHideHeader();
  return null;
}

/** Render-nothing client component that hides the global Footer. */
export function HideFooter() {
  useHideFooter();
  return null;
}
interface ProvidersProps {
  tenant: TenantConfig;
  /** `getLandvestSuccessorUrl()`, resolved by the root layout on the server. */
  landvestHandoffUrl: string;
  children: ReactNode;
}

/**
 * Client-side providers wrapper
 * Includes TenantProvider, AuthProvider, ConsentProvider, AnalyticsProvider,
 * global modals, cookie consent banner, and footer
 */
export function Providers(t0) {
  const $ = _c(55);
  const {
    tenant,
    landvestHandoffUrl,
    children
  } = t0;
  const [headerVisible, setHeaderVisible] = useState(true);
  const [footerVisible, setFooterVisible] = useState(true);
  const [slimConsent, setSlimConsent] = useState(false);
  const pathname = usePathname();
  let t1;
  if ($[0] !== pathname) {
    t1 = isTeamContactPath(pathname);
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isTeamContact = t1;
  let t2;
  if ($[2] !== isTeamContact || $[3] !== pathname) {
    t2 = isFunnelPath(pathname) || isTeamContact;
    $[2] = isTeamContact;
    $[3] = pathname;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const hideGlobalChrome = t2;
  const t3 = slimConsent || isTeamContact;
  let t4;
  if ($[5] !== t3) {
    t4 = {
      enabled: t3,
      setEnabled: setSlimConsent
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const slimConsentValue = t4;
  if (isEmbeddedIntegrationPath(pathname)) {
    let t5;
    if ($[7] !== children || $[8] !== tenant) {
      t5 = <TenantProvider tenant={tenant}>{children}</TenantProvider>;
      $[7] = children;
      $[8] = tenant;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  let t5;
  if ($[10] !== headerVisible) {
    t5 = {
      visible: headerVisible,
      setVisible: setHeaderVisible
    };
    $[10] = headerVisible;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== footerVisible) {
    t6 = {
      visible: footerVisible,
      setVisible: setFooterVisible
    };
    $[12] = footerVisible;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  let t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <SmoothScroll />;
    t8 = <GoogleAdsTag />;
    t9 = <ImpersonationBanner />;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[17] !== isTeamContact) {
    t10 = isTeamContact && <CookieConsentBanner inFlow={true} />;
    $[17] = isTeamContact;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== headerVisible || $[20] !== hideGlobalChrome) {
    t11 = headerVisible && !hideGlobalChrome && <Header />;
    $[19] = headerVisible;
    $[20] = hideGlobalChrome;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== children) {
    t12 = <div className="flex-1">{children}</div>;
    $[22] = children;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== footerVisible || $[25] !== hideGlobalChrome) {
    t13 = footerVisible && !hideGlobalChrome && <PageFooter />;
    $[24] = footerVisible;
    $[25] = hideGlobalChrome;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t12 || $[30] !== t13) {
    t14 = <div className="min-h-screen flex flex-col">{t10}{t11}{t12}{t13}</div>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <GlobalLoginModal />;
    t16 = <GlobalProfileModal />;
    $[32] = t15;
    $[33] = t16;
  } else {
    t15 = $[32];
    t16 = $[33];
  }
  let t17;
  if ($[34] !== isTeamContact) {
    t17 = !isTeamContact && <CookieConsentBanner />;
    $[34] = isTeamContact;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== isTeamContact) {
    t18 = !isTeamContact && <ChatWidget />;
    $[36] = isTeamContact;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== slimConsentValue || $[39] !== t14 || $[40] !== t17 || $[41] !== t18) {
    t19 = <SlimConsentContext.Provider value={slimConsentValue}>{t7}{t8}{t9}{t14}{t15}{t16}{t17}{t18}</SlimConsentContext.Provider>;
    $[38] = slimConsentValue;
    $[39] = t14;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t19 || $[44] !== t6) {
    t20 = <FooterContext.Provider value={t6}>{t19}</FooterContext.Provider>;
    $[43] = t19;
    $[44] = t6;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t20 || $[47] !== t5) {
    t21 = <ConsentProvider><AuthProvider><TurnstileProvider><AnalyticsProvider><HeaderContext.Provider value={t5}>{t20}</HeaderContext.Provider></AnalyticsProvider></TurnstileProvider></AuthProvider></ConsentProvider>;
    $[46] = t20;
    $[47] = t5;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== landvestHandoffUrl || $[50] !== t21) {
    t22 = <LandvestHandoffContext.Provider value={landvestHandoffUrl}>{t21}</LandvestHandoffContext.Provider>;
    $[49] = landvestHandoffUrl;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t22 || $[53] !== tenant) {
    t23 = <TenantProvider tenant={tenant}>{t22}</TenantProvider>;
    $[52] = t22;
    $[53] = tenant;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  return t23;
}

/**
 * Global login modal that responds to auth context
 */
function GlobalLoginModal() {
  const $ = _c(5);
  const {
    isLoginModalOpen,
    closeLoginModal,
    loginModalPrefill
  } = useAuth();
  const t0 = loginModalPrefill?.email;
  const t1 = loginModalPrefill?.phone;
  let t2;
  if ($[0] !== closeLoginModal || $[1] !== isLoginModalOpen || $[2] !== t0 || $[3] !== t1) {
    t2 = <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} initialEmail={t0} initialPhone={t1} />;
    $[0] = closeLoginModal;
    $[1] = isLoginModalOpen;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/**
 * Global profile modal that responds to auth context
 */
function GlobalProfileModal() {
  const $ = _c(3);
  const {
    isProfileModalOpen,
    closeProfileModal
  } = useAuth();
  let t0;
  if ($[0] !== closeProfileModal || $[1] !== isProfileModalOpen) {
    t0 = <ProfileModal isOpen={isProfileModalOpen} onClose={closeProfileModal} />;
    $[0] = closeProfileModal;
    $[1] = isProfileModalOpen;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}